import { useQuery } from "@tanstack/react-query";

import { post } from "common/helpers/HTTP";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import type { Template } from "common/types";

interface PreviewStep {
  method: "injection" | "translate";
  message: string;
  // injection step doesn't have a subject
  subject?: string;
}

type TemplatePreviewData = {
  senderProfileId: string;
  targetProfileId: string;
  template: Pick<Template, "type" | "message" | "ai_descriptors"> & {
    subject?: string;
  };
  isTranslateEnabled: boolean;
  campaignId?: string;
  sourceLanguageCode?: string;
};

async function templatePreviewRequest(
  workspaceId: string,
  payload: TemplatePreviewData,
): Promise<PreviewStep[]> {
  const {
    senderProfileId,
    targetProfileId,
    isTranslateEnabled,
    sourceLanguageCode,
    template,
    campaignId,
  } = payload;
  const { type, message, subject, ai_descriptors } = template;

  const { steps } = await post(
    `workspaces/${workspaceId}/templates-preview?detailed=true`,
    {
      account_id: senderProfileId,
      translate_flag: isTranslateEnabled,
      source_language: sourceLanguageCode,
      previewed_lead_id: targetProfileId,
      template_text: message,
      campaign_id: campaignId,
      subject,
      ai_descriptors,
      type,
    },
  );

  return steps;
}

export default function useTemplatePreview({
  previewData,
}: {
  previewData: TemplatePreviewData;
}) {
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const queryKey: (string | object)[] = ["preview", workspaceId, previewData];

  const {
    data,
    error: previewError,
    refetch: refetchPreview,
    isFetching: isLoadingPreview,
  } = useQuery({
    queryKey,
    queryFn: () => templatePreviewRequest(workspaceId, previewData),
    enabled:
      !!previewData.template &&
      !!previewData.targetProfileId &&
      !!previewData.senderProfileId,
  });

  let injectContent: PreviewStep;
  let translateContent: PreviewStep;

  if (data) {
    injectContent = data.find((step) => step.method === "injection");
    translateContent = data.find((step) => step.method === "translate");
  }

  return {
    injectContent,
    translateContent,
    isLoadingPreview,
    refetchPreview,
    previewError,
  };
}
